// admin/src/pages/ManageContacts.jsx

import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../services/adminApi';
import './ManageContacts.css';

const ManageContacts = () => {
  const [messages, setMessages]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('all');
  const [pagination, setPagination]   = useState({ page: 1, pages: 1, total: 0 });
  const [unreadCount, setUnreadCount] = useState(0);
  const [selected, setSelected]       = useState(null);
  const [deleteId, setDeleteId]       = useState(null);
  const [successMsg, setSuccessMsg]   = useState('');
  const [errorMsg, setErrorMsg]       = useState('');

  // ── Toast helpers ────────────────────────────────────────────────────────────
  const showSuccess = (msg) => { setSuccessMsg(msg); setErrorMsg('');  setTimeout(() => setSuccessMsg(''), 3000); };
  const showError   = (msg) => { setErrorMsg(msg);   setSuccessMsg(''); setTimeout(() => setErrorMsg(''),   5000); };

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await adminApi.getContactMessages({
        page,
        limit: 15,
        status: filter !== 'all' ? filter : '',
      });
      setMessages(data.data);
      setPagination(data.pagination);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      showError(err.message || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchMessages(1); }, [fetchMessages]);

  // ── Open detail (auto-marks read) ────────────────────────────────────────────
  const openMessage = async (msg) => {
    try {
      const data = await adminApi.getContactMessageById(msg._id);
      setSelected(data.data);
      setMessages((prev) => prev.map((m) => m._id === msg._id ? { ...m, status: 'read' } : m));
      if (msg.status === 'unread') setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      setSelected(msg);
    }
  };

  // ── Toggle status ────────────────────────────────────────────────────────────
  const toggleStatus = async (id, currentStatus, e) => {
    if (e) e.stopPropagation();
    const newStatus = currentStatus === 'read' ? 'unread' : 'read';
    try {
      const data = await adminApi.updateContactMessageStatus(id, newStatus);
      setMessages((prev) => prev.map((m) => m._id === id ? { ...m, status: newStatus } : m));
      setUnreadCount((c) => newStatus === 'unread' ? c + 1 : Math.max(0, c - 1));
      if (selected?._id === id) setSelected(data.data);
      showSuccess(`Marked as ${newStatus}`);
    } catch (err) {
      showError(err.message || 'Failed to update status.');
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await adminApi.deleteContactMessage(deleteId);
      setMessages((prev) => {
        const removed = prev.find((m) => m._id === deleteId);
        if (removed?.status === 'unread') setUnreadCount((c) => Math.max(0, c - 1));
        return prev.filter((m) => m._id !== deleteId);
      });
      setPagination((p) => ({ ...p, total: Math.max(0, p.total - 1) }));
      if (selected?._id === deleteId) setSelected(null);
      showSuccess('Message deleted');
    } catch (err) {
      showError(err.message || 'Failed to delete.');
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return (
      d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' · ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    );
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cm">
        <div className="cm__page-header">
          <div>
            <h1 className="cm__page-title">Contact Messages</h1>
            <p className="cm__page-desc">Loading messages…</p>
          </div>
        </div>
        <div className="cm__empty">
          <div className="cm__loader" />
          <p>Loading messages from server…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cm">

      {/* ── Page Header ── */}
      <div className="cm__page-header">
        <div>
          <h1 className="cm__page-title">Contact Messages</h1>
          <p className="cm__page-desc">
            {unreadCount > 0
              ? `${unreadCount} unread of ${pagination.total} total messages`
              : `${pagination.total} total message${pagination.total !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="cm__header-actions">
          {['all', 'unread', 'read'].map((f) => (
            <button
              key={f}
              className={`cm__filter-btn ${filter === f ? 'cm__filter-btn--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'unread' && unreadCount > 0 && <span className="cm__filter-dot" />}
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unread' && unreadCount > 0 && (
                <span className="cm__filter-count">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Toast messages ── */}
      {successMsg && (
        <div className="cm__msg cm__msg--success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="cm__msg cm__msg--error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {errorMsg}
        </div>
      )}

      {/* ── Empty state ── */}
      {messages.length === 0 ? (
        <div className="cm__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <h3>No {filter !== 'all' ? filter + ' ' : ''}messages</h3>
          <p>
            {filter !== 'all'
              ? `You have no ${filter} messages right now.`
              : 'No contact messages have been received yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* ── Table ── */}
          <div className="cm__table-wrapper">
            <table className="cm__table">
              <thead>
                <tr>
                  <th style={{ width: 16 }} />
                  <th>Name</th>
                  <th>Email</th>
                  <th className="cm__hide-sm">Subject</th>
                  <th className="cm__hide-md">Received</th>
                  <th>Status</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr
                    key={msg._id}
                    className={`cm__row ${msg.status === 'unread' ? 'cm__row--unread' : ''}`}
                    onClick={() => openMessage(msg)}
                  >
                    <td>
                      <span className={`cm__dot ${msg.status === 'unread' ? 'cm__dot--on' : ''}`} />
                    </td>
                    <td className="cm__cell-name">{msg.name}</td>
                    <td className="cm__cell-email">{msg.email}</td>
                    <td className="cm__cell-subject cm__hide-sm">{msg.subject || '—'}</td>
                    <td className="cm__cell-date cm__hide-md">{formatDate(msg.createdAt)}</td>
                    <td>
                      <span className={`cm__pill cm__pill--${msg.status}`}>{msg.status}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="cm__row-actions">
                        <button
                          className="cm__icon-btn"
                          title={msg.status === 'read' ? 'Mark unread' : 'Mark read'}
                          onClick={(e) => toggleStatus(msg._id, msg.status, e)}
                        >
                          {msg.status === 'read' ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                        <button
                          className="cm__icon-btn cm__icon-btn--danger"
                          title="Delete message"
                          onClick={(e) => { e.stopPropagation(); setDeleteId(msg._id); }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {pagination.pages > 1 && (
            <div className="cm__pagination">
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`cm__page-btn ${pagination.page === p ? 'cm__page-btn--active' : ''}`}
                  onClick={() => fetchMessages(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ MESSAGE DETAIL MODAL ═══ */}
      {selected && (
        <div className="cm__modal-overlay" onClick={() => setSelected(null)}>
          <div className="cm__modal cm__modal--detail" onClick={(e) => e.stopPropagation()}>

            <button className="cm__modal-close-btn" onClick={() => setSelected(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="cm__modal-head">
              <div className="cm__modal-head-left">
                <p className="cm__modal-eyebrow">Subject</p>
                <h2 className="cm__modal-subject">{selected.subject || 'General Enquiry'}</h2>
              </div>
              <span className={`cm__pill cm__pill--${selected.status} cm__pill--lg`}>
                {selected.status}
              </span>
            </div>

            <div className="cm__modal-meta">
              <div className="cm__meta-row">
                <span className="cm__meta-key">From</span>
                <span className="cm__meta-val">{selected.name}</span>
              </div>
              <div className="cm__meta-row">
                <span className="cm__meta-key">Email</span>
                <a href={`mailto:${selected.email}`} className="cm__meta-link">{selected.email}</a>
              </div>
              {selected.phone && (
                <div className="cm__meta-row">
                  <span className="cm__meta-key">Phone</span>
                  <a href={`tel:${selected.phone}`} className="cm__meta-link">{selected.phone}</a>
                </div>
              )}
              <div className="cm__meta-row">
                <span className="cm__meta-key">Received</span>
                <span className="cm__meta-val">{formatDate(selected.createdAt)}</span>
              </div>
            </div>

            <div className="cm__modal-body">
              <p className="cm__modal-eyebrow">Message</p>
              <p className="cm__modal-message-text">{selected.message}</p>
            </div>

            <div className="cm__modal-footer">
              <a href={`mailto:${selected.email}`} className="cm__btn cm__btn--primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                Reply
              </a>
              <button
                className="cm__btn cm__btn--ghost"
                onClick={(e) => toggleStatus(selected._id, selected.status, e)}
              >
                {selected.status === 'read' ? 'Mark Unread' : 'Mark Read'}
              </button>
              <button
                className="cm__btn cm__btn--danger"
                onClick={() => { setDeleteId(selected._id); setSelected(null); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {deleteId && (
        <div className="cm__modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="cm__modal cm__modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="cm__delete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <h3 className="cm__delete-title">Delete Message?</h3>
            <p className="cm__delete-text">
              This message will be permanently removed and cannot be recovered.
            </p>
            <div className="cm__delete-actions">
              <button className="cm__btn cm__btn--ghost" onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button className="cm__btn cm__btn--danger" onClick={confirmDelete}>
                Delete Message
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageContacts;